<script lang="ts">
  import { onMount } from "svelte";
  import CartWidgetGroupV2 from "@retailer/svelte/CartWidget/v2/CartWidgetGroupV2.svelte";
  import PickupLocationModal from "@retailer/svelte/Cart/Page/PickupOrDelivery/PickupLocationModal.svelte";
  import CartWidgetNotice from "@retailer/svelte/CartWidget/v2/CartWidgetNotice.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import RequestItem from "@retailer/svelte/CartWidget/v2/middleware/RequestItem.svelte";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";
  import { pickupWarehousesStore } from "@retailer/svelte/stores/PickupWarehousesStore";
  import RecentlyRemovedProductStore from "@retailer/stores/RecentlyRemovedProductStore";
  import CartStore from "@retailer/svelte/stores/CartStore";
  import {
    sendCartTrackingEvents,
    sendClickTracking,
  } from "@retailer/svelte/CartWidget/v2/Services/CartEventTracking";
  import { CartWidgetTargetObjectBase } from "@shared/services/event_tracking/entities";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import * as PickupWarehousesService from "@retailer/services/pickup_warehouses_service";

  import {
    Notice,
    StockLevels,
  } from "@retailer/svelte/CartWidget/v2/Services/CartItemRequestService";
  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";
  import type { CartOrderType } from "@shared/types/Cart";
  import type { CartProduct } from "@shared/types/ProductLine";

  export let inventoryId: number | null;
  export let productIndex: number;
  export let product: CartProduct;
  export let order: CartOrderType;
  export let trackingEntities: BaseEntityClass[] = [];
  export let cartProductWarehouseLocationCode: string | null;
  export let canUseWineCellar: boolean = false;

  const targetObjectTypeBase = CartWidgetTargetObjectBase.CartProduct;
  const { trackClick } = EventTracking;

  const trackCartChangeEvent = event => {
    sendCartTrackingEvents(event, trackingEntities, inventoryId);
  };

  const trackCartInteractionEvent = event => {
    sendClickTracking(targetObjectTypeBase, event, trackingEntities);
  };

  const clearRecentlyRemovedProduct = () => {
    if ($RecentlyRemovedProductStore?.id !== product.product_id) {
      RecentlyRemovedProductStore.set(null);
    }
  };

  const onUpdateQuantity = event => {
    trackCartInteractionEvent(event);
    clearRecentlyRemovedProduct();
  };

  const onProductRemoved = () => {
    RecentlyRemovedProductStore.set({
      id: product.product_id,
      index: productIndex,
      orderId: order.id,
      warehouseId: product.warehouse_id,
      distributorId: order.distributor.id,
      productLineId: product.product_line.id,
      productLineName: product.product_line.name,
    });
  };

  let showPickupSelectionModal = false;
  const onPickupClick = async () => {
    trackClick("check_pickup_availability", trackingEntities);
    await PickupWarehousesService.get(product.product_id, order.distributor.id);
    showPickupSelectionModal = true;
  };

  const onPickupSelection = async selectedPickupLocation => {
    const quantityToAdd = 1;

    const unitQuantity = cartWidgetItem.sold_by.includes("units") ? quantityToAdd : 0;
    const caseQuantity = unitQuantity ? 0 : quantityToAdd;

    await InventoryItemStore.addToCart(inventoryId, {
      inventory_id: inventoryId,
      unit_quantity: unitQuantity,
      case_quantity: caseQuantity,
      warehouse_id: selectedPickupLocation.id,
    });
    await CartStore.load();
    closeModal();
  };
  const closeModal = () => {
    showPickupSelectionModal = false;
  };

  let loaded: boolean = false;

  onMount(async () => {
    if (inventoryId) {
      loaded = false;
      const response = await InventoryItemStore.fetchInventoryItem(
        inventoryId,
        cartProductWarehouseLocationCode,
      );
      loaded = response?.loaded;
    }
  });

  $: cartWidgetItem = $InventoryItemStore?.inventoryItems[inventoryId];
  $: informationalNotices = cartWidgetItem?.notices.filter((notice: Notice) => {
    return notice.level === "info" && notice.message;
  });
  $: notices = cartWidgetItem?.notices.filter(notice => notice.level !== "info" && notice.message);
  $: noticesIncludeErrors = notices?.some(notice => notice.level === "error");
  $: pickUpOptionsAvailable =
    cartWidgetItem?.stock_info?.additional_action?.endsWith("pickup_available");
  $: cartProductIdDoesNotMatchInventory =
    product.product_id !== cartWidgetItem?.product_summary?.id;
  $: showSeeOtherSizes =
    (!cartWidgetItem?.buying_options.length &&
      (noticesIncludeErrors || cartWidgetItem?.stock_info?.level === StockLevels.outOfStock)) ||
    cartProductIdDoesNotMatchInventory;
  $: fulfillmentMethod = {
    distributor_id: order.distributor.id,
    retailer_id: order.retailer_id,
    warehouse_id: product.warehouse_id,
  };

  $: needsAccountNumber =
    cartWidgetItem?.stock_info?.additional_action?.endsWith("connect_account");
</script>

<style>
  .container-div {
    display: flex;
    flex-direction: column;
  }

  @media (min-width: 700px) {
    .desktop-width {
      max-width: 250px;
    }
  }
</style>

{#if loaded && cartWidgetItem}
  <RequestItem {cartWidgetItem} fulfillmentWarehouseId={product.warehouse_id} isCartPage={true}>
    <div class="fizz-flow-8 container-div" slot="cart-widget">
      {#each notices as notice}
        <CartWidgetNotice {notice} />
      {/each}
      {#if cartProductIdDoesNotMatchInventory}
        <span class="fizz-text-negative fizz-flex-row-4">
          <Icon className="fizz-icon-16" type="Info" />
          <p>This size is unavailable. Remove this item.</p>
        </span>
      {:else}
        <CartWidgetGroupV2
          widgetItem={cartWidgetItem}
          displayAsRow={true}
          {fulfillmentMethod}
          fulfillmentWarehouseId={product.warehouse_id}
          showAltFulfillmentNotice={false}
          on:cartWidgetChange={trackCartChangeEvent}
          on:updateQuantity={onUpdateQuantity}
          on:productRemoved={onProductRemoved}
        />
      {/if}
      {#if !canUseWineCellar && pickUpOptionsAvailable && !cartWidgetItem.buying_options.length && !needsAccountNumber}
        <button
          on:click|preventDefault={onPickupClick}
          class="fizz-button fizz-full-width desktop-width"
        >
          View more availability
        </button>
      {/if}
      {#if showSeeOtherSizes}
        <a
          class="fizz-button fizz-full-width desktop-width"
          href="/products/{product.product_line.id}"
        >
          See other sizes
        </a>
      {/if}
      {#each informationalNotices as notice}
        <CartWidgetNotice {notice} />
      {/each}
    </div>
  </RequestItem>
{:else if !inventoryId}
  <div class="fizz-flow-8 container-div">
    <span class="fizz-text-negative fizz-flex-row-4">
      <Icon className="fizz-icon-16" type="Info" />
      <p>This size is unavailable. Remove this item.</p>
    </span>
    <a class="fizz-button fizz-full-width desktop-width" href="/products/{product.product_line.id}">
      See other sizes
    </a>
  </div>
{/if}

{#if showPickupSelectionModal && $pickupWarehousesStore.loaded}
  <PickupLocationModal
    selectedPickupLocationId={null}
    pickupOptions={$pickupWarehousesStore.pickupWarehouses}
    distributor={order.distributor}
    onCancel={closeModal}
    {onPickupSelection}
    shouldShowLowStock={pickUpOptionsAvailable}
  />
{/if}
